import { useState, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

// Debug logging helper
const debug = process.env.NODE_ENV !== 'production' 
  ? (...args) => console.log('[DeviceDetection]', ...args)
  : () => {};

// Debounce helper
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const useEnhancedDeviceDetection = () => {
  // Track hydration state
  const [isHydrated, setIsHydrated] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });

  // Media queries
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const isLandscape = useMediaQuery({ query: '(orientation: landscape)' });
  const isMobileView = useMediaQuery({ maxWidth: 767 });
  const isTabletView = useMediaQuery({ minWidth: 768, maxWidth: 1024 });
  const isDesktopView = useMediaQuery({ minWidth: 1025 });

  // User agent and platform state
  const [userAgent, setUserAgent] = useState('');
  const [platform, setPlatform] = useState('');

  // Handle window resize with debounce
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    const debouncedResize = debounce(handleResize, 100);
    window.addEventListener('resize', debouncedResize);
    
    // Initial call
    handleResize();
    
    return () => window.removeEventListener('resize', debouncedResize);
  }, []);

  // Initialize on client side - immediate initialization for better responsiveness
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const init = () => {
      setUserAgent(navigator.userAgent);
      setPlatform(navigator.platform);
      setIsHydrated(true);
      setIsReady(true);
      
      debug('Device detection initialized:', {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        width: window.innerWidth,
        height: window.innerHeight,
        orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
      });
    };

    // Initialize immediately when DOM is ready, don't wait for full page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return () => document.removeEventListener('DOMContentLoaded', init);
    } else {
      // DOM is already ready, initialize immediately
      init();
    }
  }, []);

  // Calculate device info
  const deviceInfo = useMemo(() => {
    // Default values for SSR or before hydration
    if (!isHydrated) {
      debug('SSR mode - returning default values');
      return getDefaultDeviceInfo();
    }

    const { width, height } = windowSize;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                 (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(userAgent);
    const isTouchDevice = 'ontouchstart' in window || 
                         navigator.maxTouchPoints > 0 || 
                         navigator.msMaxTouchPoints > 0;

    // Unified device type detection
    let deviceType = 'desktop';
    if (width <= 767 || (isIOS && width <= 991) || (isAndroid && width <= 991)) {
      deviceType = 'mobile';
    } else if ((width >= 768 && width <= 1024) || 
              (isIOS && width <= 1366) || 
              (isAndroid && width <= 1024)) {
      deviceType = 'tablet';
    }

    // Log device info for debugging
    debug('Device info:', {
      width,
      height,
      userAgent,
      platform,
      deviceType,
      isMobileView,
      isTabletView,
      isDesktopView,
      isPortrait,
      isLandscape
    });

    return {
      // Screen size
      isMobile: deviceType === 'mobile',
      isTablet: deviceType === 'tablet',
      isDesktop: deviceType === 'desktop',
      
      // Device type
      isIOS,
      isAndroid,
      isMobileDevice: deviceType === 'mobile' || deviceType === 'tablet',
      isTouchDevice,
      deviceType,
      
      // Orientation
      orientation: isPortrait ? 'portrait' : 'landscape',
      isPortrait,
      isLandscape,
      
      // State
      isReady: isReady && isHydrated,
      isHydrated,
      
      // Raw values for debugging
      _raw: {
        userAgent,
        platform,
        width,
        height
      }
    };
  }, [
    isHydrated,
    isReady,
    userAgent,
    platform,
    windowSize,
    isMobileView,
    isTabletView,
    isDesktopView,
    isPortrait,
    isLandscape
  ]);

  return deviceInfo;
};

// Helper function to get default device info for SSR - mobile-first approach
const getDefaultDeviceInfo = () => {
  // Default to mobile for better first load experience
  const defaultWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
  const isMobileDefault = defaultWidth <= 767;
  
  return {
    isMobile: isMobileDefault,
    isTablet: !isMobileDefault && defaultWidth <= 1024,
    isDesktop: !isMobileDefault && defaultWidth > 1024,
    isIOS: false,
    isAndroid: false,
    isMobileDevice: isMobileDefault,
    isTouchDevice: false,
    deviceType: isMobileDefault ? 'mobile' : 'desktop',
    orientation: 'portrait',
    isPortrait: true,
    isLandscape: false,
    isReady: false,
    isHydrated: false,
    _raw: {
      userAgent: '',
      platform: '',
      width: defaultWidth,
      height: typeof window !== 'undefined' ? window.innerHeight : 667
    }
  };
};

export default useEnhancedDeviceDetection;
